/**
 * User Telemetry Card
 * Displays per-user token usage and cost data for admin viewing
 * Available to system_admin and org_admin on user detail pages
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CardComponentProps } from '@/lib/cards';
import {
    getAdminTelemetryUsage,
    type AdminTelemetryGranularity,
    type AdminTelemetryResponse,
    type TelemetryPeriod,
} from '@/api/billing';

const PERIOD_LABELS: Record<TelemetryPeriod, string> = {
    current_month: 'This month',
    last_month: 'Last month',
    all: 'All time',
};

const GRANULARITY_LABELS: Record<AdminTelemetryGranularity, string> = {
    month: 'By month',
    day: 'By day',
};

/**
 * User Telemetry Card
 * Displays usage metrics (token counts, API calls, estimated cost) for a specific user
 */
export const UserTelemetryCard: React.FC<CardComponentProps> = ({
    card,
    userContext,
    onError,
}) => {
    const { userId: routeUserId } = useParams<{ userId: string }>();
    const [period, setPeriod] = useState<TelemetryPeriod>('current_month');
    const [granularity, setGranularity] = useState<AdminTelemetryGranularity>('month');
    const [data, setData] = useState<AdminTelemetryResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Extract target user ID from route params (user being viewed on detail page)
    const targetUserId = routeUserId || userContext.userId;

    useEffect(() => {
        if (!targetUserId) {
            setError('No user ID available');
            setLoading(false);
            return;
        }

        let active = true;

        async function load() {
            setLoading(true);
            setError(null);
            try {
                const res = await getAdminTelemetryUsage(targetUserId, period, granularity);
                if (!active) return;
                setData(res);
            } catch (err) {
                if (!active) return;
                const errorMsg = err instanceof Error ? err.message : 'Failed to load telemetry data';
                setError(errorMsg);
                onError?.(err instanceof Error ? err : new Error(errorMsg));
            } finally {
                if (active) setLoading(false);
            }
        }

        void load();
        return () => {
            active = false;
        };
    }, [targetUserId, period, granularity, onError]);

    const dateColumnLabel = granularity === 'month' ? 'Month' : 'Date';

    return (
        <div className="card card-surface mt-4">
            <div className="card-header bg-transparent border-0 pb-2 d-flex flex-wrap align-items-center gap-2">
                <span className="fw-semibold me-auto">Usage telemetry</span>
                <div className="btn-group btn-group-sm" role="group" aria-label="Period">
                    {(Object.keys(PERIOD_LABELS) as TelemetryPeriod[]).map((p) => (
                        <button
                            key={p}
                            type="button"
                            className={`btn ${period === p ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setPeriod(p)}
                        >
                            {PERIOD_LABELS[p]}
                        </button>
                    ))}
                </div>
                <div className="btn-group btn-group-sm" role="group" aria-label="Granularity">
                    {(Object.keys(GRANULARITY_LABELS) as AdminTelemetryGranularity[]).map((g) => (
                        <button
                            key={g}
                            type="button"
                            className={`btn ${granularity === g ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            onClick={() => setGranularity(g)}
                        >
                            {GRANULARITY_LABELS[g]}
                        </button>
                    ))}
                </div>
            </div>
            <div className="card-body">
                {loading && (
                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                )}
                {!loading && error && (
                    <div className="alert alert-danger mb-0" role="alert">{error}</div>
                )}
                {!loading && !error && data && (
                    <>
                        <dl className="row mb-0">
                            <dt className="col-sm-6 text-muted fw-normal" style={{ fontSize: '0.85rem' }}>
                                Emails processed
                            </dt>
                            <dd className="col-sm-6">{data.rows.reduce((sum, r) => sum + r.emails_processed_total, 0).toLocaleString()}</dd>

                            <dt className="col-sm-6 text-muted fw-normal" style={{ fontSize: '0.85rem' }}>
                                Graph API calls
                            </dt>
                            <dd className="col-sm-6">{data.rows.reduce((sum, r) => sum + r.graph_calls_total, 0).toLocaleString()}</dd>

                            <dt className="col-sm-6 text-muted fw-normal" style={{ fontSize: '0.85rem' }}>
                                Bedrock calls
                            </dt>
                            <dd className="col-sm-6">{data.rows.reduce((sum, r) => sum + r.bedrock_calls_total, 0).toLocaleString()}</dd>

                            <dt className="col-sm-6 text-muted fw-normal" style={{ fontSize: '0.85rem' }}>
                                Bedrock tokens
                            </dt>
                            <dd className="col-sm-6">{data.rows.reduce((sum, r) => sum + r.bedrock_tokens_total, 0).toLocaleString()}</dd>

                            <dt className="col-sm-6 text-muted fw-normal" style={{ fontSize: '0.85rem' }}>
                                Estimated cost (USD)
                            </dt>
                            <dd className="col-sm-6">
                                ${data.rows.reduce((sum, r) => sum + r.estimated_cost_usd_total, 0).toFixed(4)}
                            </dd>
                        </dl>

                        {data.rows.length > 1 && (
                            <div className="table-responsive mt-3">
                                <table
                                    className="table table-sm table-borderless table-white mb-0"
                                    style={{ fontSize: '0.85rem' }}
                                >
                                    <thead>
                                        <tr>
                                            <th className="text-muted fw-normal">{dateColumnLabel}</th>
                                            <th className="text-muted fw-normal text-end">Emails</th>
                                            <th className="text-muted fw-normal text-end">Graph calls</th>
                                            <th className="text-muted fw-normal text-end">Bedrock calls</th>
                                            <th className="text-muted fw-normal text-end">Tokens</th>
                                            <th className="text-muted fw-normal text-end">Cost (USD)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.rows.map((row) => {
                                            const dateKey = row.year_month || row.event_date || 'N/A';
                                            return (
                                                <tr key={dateKey}>
                                                    <td>{dateKey}</td>
                                                    <td className="text-end">{row.emails_processed_total.toLocaleString()}</td>
                                                    <td className="text-end">{row.graph_calls_total.toLocaleString()}</td>
                                                    <td className="text-end">{row.bedrock_calls_total.toLocaleString()}</td>
                                                    <td className="text-end">{row.bedrock_tokens_total.toLocaleString()}</td>
                                                    <td className="text-end">${row.estimated_cost_usd_total.toFixed(4)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <p className="text-muted mb-0 mt-3" style={{ fontSize: '0.75rem' }}>
                            Source: {data._source.view} · as of {new Date(data._source.asOf).toLocaleString('en-GB')}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default UserTelemetryCard;
